import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useCountUp } from "@/hooks/useCountUp";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ExternalLink, Pill, Users, TrendingUp, Leaf, BarChart3 } from "lucide-react";

const facts = [
  {
    icon: Pill,
    statValue: 285,
    statSuffix: "",
    statDisplay: "285",
    label: "Tagesdosen Medikamente bekommt jeder Deutsche pro Jahr verschrieben",
    source: "TK-Auswertung 2024",
    isProblem: true,
    detailTitle: "285 Tagesdosen Medikamente pro Jahr",
    detailDescription: "Jeder Deutsche bekommt pro Jahr durchschnittlich 285 Tagesdosen Medikamente verschrieben – Tendenz steigend. Diese alarmierende Zahl zeigt, wie stark unser Gesundheitssystem auf Medikamente setzt, statt auf Prävention und natürliche Heilmethoden.",
    detailPoints: [
      "Antibiotika werden oft unnötig verschrieben und fördern Resistenzen",
      "Schmerzmittel können zur Abhängigkeit führen und schädigen Organe",
      "Viele Nebenwirkungen werden unterschätzt oder nicht erkannt",
      "Die Wechselwirkungen verschiedener Medikamente sind oft unerforscht"
    ],
    detailSourceLink: "https://www.tk.de/presse/themen/arzneimittel/verordnungen/hoechstwert-arzneimittelverordnungen-2201692"
  },
  {
    icon: Users,
    statValue: 70,
    statSuffix: "%",
    statDisplay: "70%",
    label: "der Deutschen leben mit chronischen Erkrankungen",
    source: "Statista Consumer Insights 2024",
    isProblem: true,
    detailTitle: "70% leiden unter chronischen Erkrankungen",
    detailDescription: "Mehr als zwei Drittel der Deutschen leben mit mindestens einer chronischen Erkrankung. Diabetes, Bluthochdruck, Allergien und Autoimmunerkrankungen nehmen kontinuierlich zu – trotz modernster Medizin.",
    detailPoints: [
      "Diabetes, Bluthochdruck und Allergien nehmen stetig zu",
      "Lifestyle-Faktoren wie Ernährung und Stress spielen eine große Rolle",
      "Prävention wird im Gesundheitssystem oft vernachlässigt",
      "Viele chronische Leiden wären durch natürliche Mittel vermeidbar"
    ],
    detailSourceLink: "https://de.statista.com/infografik/33272/umfrage-zu-physischen-einschraenkungen-und-chronischen-erkrankungen-in-deutschland/"
  },
  {
    icon: TrendingUp,
    statValue: 0,
    statSuffix: "",
    statDisplay: "Platz 1",
    label: "bei Arztbesuchen in Europa – aber nur Mittelfeld bei Lebenserwartung",
    source: "OECD Health at a Glance Europe 2024",
    isProblem: true,
    noAnimation: true,
    detailTitle: "Platz 1 bei Arztbesuchen – Mittelfeld bei Gesundheit",
    detailDescription: "Deutsche gehen öfter zum Arzt als jedes andere europäische Land – durchschnittlich 17 Mal pro Jahr. Trotzdem liegt die Lebenserwartung und Lebensqualität nur im europäischen Mittelfeld. Ein Paradox, das zum Nachdenken anregt.",
    detailPoints: [
      "17 Arztbesuche pro Jahr im Durchschnitt – Spitze in Europa",
      "Fokus auf Symptombehandlung statt Ursachenforschung",
      "Wenig Zeit für präventive Beratung und ganzheitliche Ansätze",
      "Hohe Kosten bei vergleichsweise mittelmäßigen Gesundheitsergebnissen"
    ],
    detailSourceLink: "https://www.oecd.org/en/publications/health-at-a-glance-europe-2024_b3704e14-en.html"
  },
  {
    icon: Leaf,
    statValue: 150,
    statSuffix: "+",
    statDisplay: "150+",
    label: "Naturmittel von A-Z in diesem Buch erklärt",
    source: "Dieses Buch",
    isProblem: false,
    detailTitle: "150+ natürliche Mittel von A-Z",
    detailDescription: "Dieses Buch vereint über 150 natürliche Mittel, übersichtlich sortiert von A bis Z. Jedes Mittel wird wissenschaftlich fundiert erklärt und ist sofort in der Praxis anwendbar – ohne gefährliche Nebenwirkungen.",
    detailPoints: [
      "Wissenschaftlich fundiert und verständlich erklärt",
      "Sofort anwendbare Anleitungen für den Alltag",
      "Keine gefährlichen Nebenwirkungen wie bei Medikamenten",
      "Traditionelles Heilwissen trifft moderne Forschung"
    ]
  }
];

const ProblemSection = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const [selectedFact, setSelectedFact] = useState<typeof facts[0] | null>(null);

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-4 shadow-sm">
              <BarChart3 className="w-5 h-5 text-primary" />
              <span className="text-sm font-semibold text-primary">Die Fakten</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground">
              Wir werden kränker, nicht gesünder
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Wir schlucken immer mehr Pillen – und werden trotzdem nicht gesünder. 
              Zeit, einen anderen Weg zu gehen.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mb-12">
            {facts.map((fact, index) => {
              const FactCounter = () => {
                const count = useCountUp({
                  endValue: fact.statValue,
                  duration: 2000,
                  isVisible,
                  suffix: fact.statSuffix
                });
                return <>{fact.noAnimation ? fact.statDisplay : count}</>;
              };

              return (
                <div
                  key={index}
                  onClick={() => setSelectedFact(fact)}
                  className={`p-6 rounded-xl border shadow-soft hover:shadow-hover hover:-translate-y-0.5 transition-all duration-500 group cursor-pointer ${
                    fact.isProblem 
                      ? 'bg-muted/30 border-border/30 hover:border-border/50' 
                      : 'bg-primary/5 border-primary/30 hover:border-primary/50 shadow-[0_0_20px_rgba(34,197,94,0.1)]'
                  }`}
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                    transitionDelay: `${index * 100}ms`
                  }}
                >
                  <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${
                    fact.isProblem 
                      ? 'bg-muted group-hover:bg-muted/80' 
                      : 'bg-primary/15 group-hover:bg-primary/20'
                  }`}>
                    <fact.icon className={`w-7 h-7 ${fact.isProblem ? 'text-foreground/70' : 'text-primary'} animate-float-gentle`} />
                  </div>
                    <div className="flex-1">
                      <div className={`text-4xl md:text-5xl font-bold mb-2 ${
                        fact.isProblem 
                          ? 'text-foreground drop-shadow-[0_0_8px_rgba(255,255,255,0.15)]' 
                          : 'text-primary drop-shadow-[0_0_12px_rgba(34,197,94,0.3)]'
                      }`}>
                        <FactCounter />
                      </div>
                      <div className="text-sm text-foreground/90 font-medium mb-2 leading-snug">{fact.label}</div>
                      <div className="text-xs text-muted-foreground italic">Quelle: {fact.source}</div>
                      <div className="text-xs text-primary font-medium mt-2 flex items-center gap-1">
                        Mehr erfahren
                        <svg className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/10 text-center space-y-4">
            <p className="text-xl text-foreground font-semibold">
              Dieses Buch zeigt natürliche Alternativen.
            </p>
            <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Es vereint traditionelles Heilwissen mit modernen wissenschaftlichen Studien – 
              übersichtlich, verständlich und sofort anwendbar.
            </p>
          </div>
        </div>
      </div>

      <Dialog open={!!selectedFact} onOpenChange={() => setSelectedFact(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedFact && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4 mb-4">
                  <div className={`flex-shrink-0 w-16 h-16 rounded-xl flex items-center justify-center ${
                    selectedFact.isProblem 
                      ? 'bg-muted' 
                      : 'bg-primary/15'
                  }`}>
                    <selectedFact.icon className={`w-10 h-10 ${selectedFact.isProblem ? 'text-foreground/70' : 'text-primary'}`} />
                  </div>
                  <div className="flex-1">
                    <div className={`text-5xl font-bold mb-2 ${
                      selectedFact.isProblem ? 'text-foreground' : 'text-primary'
                    }`}>
                      {selectedFact.statDisplay}
                    </div>
                  </div>
                </div>
                <DialogTitle className="text-2xl font-bold">{selectedFact.detailTitle}</DialogTitle>
                <DialogDescription className="text-base leading-relaxed pt-2">
                  {selectedFact.detailDescription}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 pt-4">
                {selectedFact.detailPoints.map((point, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${
                      selectedFact.isProblem 
                        ? 'bg-muted text-foreground' 
                        : 'bg-primary/15 text-primary'
                    }`}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-sm text-foreground/90 leading-relaxed">{point}</p>
                  </div>
                ))}
              </div>

              {selectedFact.detailSourceLink && (
                <div className="pt-6 border-t border-border/50">
                  <a 
                    href={selectedFact.detailSourceLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Quelle ansehen: {selectedFact.source}
                  </a>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ProblemSection;
